import type { ProviderId } from "../agent-protocol.ts";

export type AuthorityRef =
  | { kind: "current_device" }
  | { kind: "device"; deviceId: string }
  | { kind: "cloud_user"; userId: string }
  | { kind: "cloud_organization"; organizationId: string };

export interface ResourceRef<T extends ResourceType = ResourceType> {
  resourceType: T;
  resourceId: string;
  authority: AuthorityRef;
}

export type ResourceType = "session" | "note";

export type QueryScope =
  | { kind: "current_device" }
  | { kind: "device"; deviceId: string }
  | { kind: "all_devices" };

/**
 * Legacy providers remain source-compatible while future Agent identifiers
 * can be projected as opaque values by the SDK. Execution code must still
 * establish Adapter support before launching or sending to one.
 */
export type { ProviderId } from "../agent-protocol.ts";
export type SessionManagementMode = "native";
export type SessionStatus =
  | "starting"
  | "running"
  | "waiting"
  | "stopping"
  | "stopped"
  | "exited"
  | "error"
  | "archived";
export type ProviderReadiness = "unknown" | "probing" | "ready" | "degraded" | "unavailable";
export type SessionAttention = "none" | "info" | "action" | "critical";
export type SessionVisibility = "attached" | "background" | "hidden";

/**
 * Derive the stable display title used when a caller starts a Session with a
 * first instruction but leaves the optional title blank. Keep this at the
 * shared creation boundary so Local, Cloud and Desktop cannot disagree.
 */
export function sessionTitleFromText(value: string | undefined): string {
  const firstLine = (value ?? "").split(/\r?\n/u, 1)[0] ?? "";
  const compact = firstLine.replace(/[ \t]+/gu, " ").trim();
  const characters = Array.from(compact);
  if (characters.length <= 80) return compact;
  return `${characters.slice(0, 79).join("")}…`;
}

export interface SessionDetail extends SessionSummary {
  processAttemptId: string | null;
  permissionMode: "read-only" | "workspace-write" | "full-access";
}

/** Explicitly requested current working directory for a Session. */
export interface SessionWorkspace {
  path: string;
}

export interface SessionCreateInput {
  /** Enrolled computer that will own this Session when using Cloud. */
  targetDeviceId?: string;
  /** Stable caller-assigned identity used to reconcile unknown remote completion. */
  sessionId?: string;
  title?: string;
  provider: ProviderId;
  managementMode?: SessionManagementMode;
  workspaceId?: string;
  permissionMode?: "read-only" | "workspace-write" | "full-access";
  workspaceMode?: "existing" | "session-id" | "custom";
  workspaceSubdirectory?: string;
  model?: string;
  startImmediately?: boolean;
  /** Stable command identity for the automatic initial sessions.start phase. */
  startOperationId?: string;
}

/** Stable identity for an idempotent Native Session start command. */
export interface SessionStartInput {
  /** Stable command identity reused when an indeterminate start is retried. */
  operationId?: string;
}

export interface TerminalHistoryQuery {
  beforeSequence?: number;
  afterSequence?: number;
  limit?: number;
}

export interface TerminalFrame {
  id: string;
  sequence: number;
  timestamp: string;
  kind: "output" | "snapshot" | "input";
  text: string;
}

export interface TerminalHistoryResult {
  sessionId: string;
  frames: TerminalFrame[];
  nextCursor: number | null;
  /** Metadata for bounded snapshots; absent for older transports. */
  generation?: number;
  truncated?: boolean;
  hasEarlier?: boolean;
}

export type SessionRecoveryAction = "continue" | "restart" | "dismiss";

export interface SessionRecoveryInput {
  incidentId?: string;
  action?: SessionRecoveryAction;
}

export interface RecoveryRecord {
  cause: string;
  error: string;
  attemptedAt: string;
  attemptNumber: number;
  outcome: "scheduled" | "dispatched" | "succeeded" | "cancelled" | "failed";
  detail: string | null;
  completedAt: string | null;
}

export interface AutoContinuePolicy {
  enabled: boolean;
  recoveryEnabled: boolean;
  retryWindowSeconds: number;
  retryForever: boolean;
  retryIntervalSeconds: number;
  attemptsUsed: number;
  recoveryStartedAt: string | null;
  lastRecoveryError: string | null;
  recoveryHistory: RecoveryRecord[];
}

export interface ControllerState {
  mode: "available" | "owned" | "held-elsewhere" | "unavailable";
  holderLabel: string | null;
  leaseId: string | null;
  expiresAt: string | null;
  generation: number;
}

export interface WorkspaceRef {
  id: string;
  name: string;
  path: string;
  branch: string | null;
}

export interface SessionSource {
  authority: AuthorityRef;
  syncState: "current" | "stale";
}

export interface SessionSummary {
  ref: ResourceRef<"session">;
  id: string;
  shortId: string;
  /** Display label of the owning enrolled computer when projected remotely. */
  deviceName?: string;
  title: string;
  provider: ProviderId;
  /** Canonical Agent identity when supplied by the authority. */
  agentId?: string;
  cliVariantId?: string;
  managementMode: SessionManagementMode;
  model: string;
  status: SessionStatus;
  /** Provider readiness is distinct from the native process state. */
  providerReadiness?: ProviderReadiness;
  attention: SessionAttention;
  visibility: SessionVisibility;
  workspace: WorkspaceRef;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string;
  unreadCount: number;
  /** Current process attempt when the owning Daemon exposes one. */
  processAttemptId?: string | null;
  generation: number;
  activeStageId: string | null;
  taskProgress: number | null;
  autoContinue: AutoContinuePolicy;
  controller: ControllerState;
  preview: string;
  tags: string[];
  source: SessionSource;
}

export interface SessionListQuery {
  search?: string;
  providers?: ProviderId[];
  statuses?: SessionStatus[];
  attention?: SessionAttention[];
  workspaceIds?: string[];
  includeArchived?: boolean;
  sort?: "activity-desc" | "activity-asc" | "created-desc" | "title-asc";
  /** Bounded list window used by compact/mobile clients. */
  limit?: number;
  /** Zero-based window offset. Callers should normally grow limit from zero. */
  offset?: number;
}

export interface SessionListRequest {
  scope?: QueryScope;
  query?: SessionListQuery;
}

export interface SessionListResult {
  sessions: SessionSummary[];
  total: number;
  revision: number;
}

export interface CommandReceipt {
  id: string;
  command:
    | "sessions.create"
    | "sessions.rename"
    | "sessions.start"
    | "sessions.stop"
    | "sessions.restart"
    | "sessions.pause"
    | "sessions.recover"
    | "sessions.delete"
    | "sessions.workspace"
    | "sessions.set_auto_continue"
    | "providers.inventory.get"
    | "providers.inventory.refresh"
    | "providers.settings.update"
    | "agents.install"
    | "agents.uninstall"
    | "agents.upgrade"
    | "agents.enable"
    | "agents.disable"
    | "settings.get"
    | "settings.update"
    | "api.manager.snapshot"
    | "api.manager.read"
    | "api.manager.request"
    | "provider_history.discover"
    | "provider_history.scan"
    | "provider_history.scan.create"
    | "provider_history.scan.get"
    | "provider_history.scan.cancel"
    | "provider_history.scan.resume"
    | "provider_history.read"
    | "provider_history.import"
    | "recovery.incidents.list"
    | "recovery.incident.resolve"
    | "directory.pick"
    | "directory.list";
  status: "accepted" | "running" | "succeeded" | "failed" | "rejected";
  requestedAt: string;
  completedAt: string | null;
  detail: string;
}

export type SessionListEvent =
  | { type: "snapshot"; result: SessionListResult }
  | { type: "session.upserted"; revision: number; session: SessionSummary }
  | { type: "session.deleted"; revision: number; ref: ResourceRef<"session"> }
  | {
      type: "gap";
      requestedAfterRevision: number;
      earliestAvailableRevision: number;
      latestRevision: number;
    };

export const CURRENT_DEVICE_AUTHORITY: AuthorityRef = { kind: "current_device" };

export function currentDeviceSessionRef(sessionId: string): ResourceRef<"session"> {
  return {
    resourceType: "session",
    resourceId: sessionId,
    authority: CURRENT_DEVICE_AUTHORITY,
  };
}

export function resourceKey(ref: ResourceRef): string {
  return `${authorityKey(ref.authority)}:${ref.resourceType}:${ref.resourceId}`;
}

export function authorityKey(authority: AuthorityRef): string {
  if (authority.kind === "current_device") return "current_device";
  if (authority.kind === "device") return `device:${authority.deviceId}`;
  if (authority.kind === "cloud_user") return `cloud_user:${authority.userId}`;
  return `cloud_organization:${authority.organizationId}`;
}
